import { useState, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card, KpiCard } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import {
  BarChart2,
  DollarSign,
  Target,
  TrendingUp,
  Globe,
  Users,
  Megaphone,
  Tag,
  Search,
} from 'lucide-react';
import { formatNumber, formatCurrency, formatPercent } from '../lib/utils';
import { CountryFlag } from '../components/ui/CountryFlag';
import { SubIdDetailModal } from '../components/SubIdDetailModal';
import { usePollMultiplier } from '../hooks/usePollMultiplier';

// ─── Types ────────────────────────────────────────────────────────────────────

const ranges = ['today', 'yesterday', '7d', '30d', 'custom'] as const;
type Range = (typeof ranges)[number];

function utcDateInput(ts = Date.now()): string {
  return new Date(ts).toISOString().slice(0, 10);
}

const dimensions = ['Publisher', 'Campaign', 'Offer', 'Country'] as const;
type Dimension = (typeof dimensions)[number];

interface OverviewData {
  clicksTotal: number;
  clicksQuality: number;
  conversionsApproved: number;
  revenueGross: number;
  payoutGross: number;
  profit: number;
  cr: number;
}

interface ByPublisherRow {
  publisherId: number | null;
  clicksTotal: number;
  clicksQuality: number;
  conversionsApproved: number;
  revenueGross: number;
  payoutGross: number;
  /** Present for clicks-only sub_ids that aren't registered as a publisher. */
  subIdHint?: string;
}

interface ByCampaignRow {
  campaignId: number | null;
  clicksTotal: number;
  clicksQuality: number;
  conversionsApproved: number;
  revenueGross: number;
  payoutGross: number;
}

interface ByOfferRow {
  offerId: number | null;
  clicksTotal: number;
  clicksQuality: number;
  conversionsApproved: number;
  revenueGross: number;
  payoutGross: number;
}

interface ByCountryRow {
  country: string | null;
  clicksTotal: number;
  conversionsApproved: number;
  revenueGross: number;
}

interface Publisher {
  id: number;
  name?: string;
  displayName?: string;
  email?: string;
}

interface Campaign {
  id: number;
  displayName: string;
}

interface Offer {
  id: number;
  name: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cr(clicks: number, convs: number): string {
  if (!clicks) return '0.00%';
  return formatPercent((convs / clicks) * 100, 2);
}

function profit(rev: number, payout: number): number {
  return rev - payout;
}

// ─── Loading skeleton row ─────────────────────────────────────────────────────

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i}>
          <div className="h-3 bg-bg-tertiary rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

// ─── Publisher table ──────────────────────────────────────────────────────────

function PublisherTable({
  data,
  publishers,
  isLoading,
  onRowClick,
  search,
}: {
  data: ByPublisherRow[];
  publishers: Publisher[];
  isLoading: boolean;
  /** Open the per-Sub-ID drill-down modal. Pass `null` for the unlinked
   *  group; if the row carries a `subIdHint` (unlinked-but-known-sub_id),
   *  also pass that so the modal can scope to that sub_id rather than the
   *  whole unlinked aggregate. */
  onRowClick?: (publisherId: number | null, subIdHint?: string) => void;
  search: string;
}) {
  const nameMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of publishers) {
      m.set(p.id, p.displayName ?? p.name ?? p.email ?? `#${p.id}`);
    }
    return m;
  }, [publishers]);

  // Client-side filter. Keeps the row when search is empty or any matchable
  // string (display name, sub_id hint, "#id") contains the query (case-insensitive).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const label =
        row.publisherId != null
          ? (nameMap.get(row.publisherId) ?? `#${row.publisherId}`)
          : (row.subIdHint ?? 'unlinked');
      return (
        label.toLowerCase().includes(q) ||
        String(row.publisherId ?? '').includes(q)
      );
    });
  }, [data, nameMap, search]);

  return (
    <div className="overflow-x-auto">
      <table className="table-cyber">
        <thead>
          <tr>
            <th>Publisher</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Quality</th>
            <th className="text-right">Convs</th>
            <th className="text-right">CR</th>
            <th className="text-right" title="Yang dibayar network ke kita">
              Revenue
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={6} />
            ))}
          {!isLoading &&
            filtered.map((row, i) => {
              // Three label cases:
              //   1. Registered publisher → display name from publishers table
              //   2. Unregistered click-only sub_id → plain sub_id text (white)
              //   3. NULL-publisher conversions (orphan) → "Unlinked" italic
              const name =
                row.publisherId != null
                  ? (nameMap.get(row.publisherId) ?? `#${row.publisherId}`)
                  : row.subIdHint
                    ? row.subIdHint
                    : 'Unlinked';
              const isUnregistered = row.publisherId == null && !!row.subIdHint;
              return (
                <tr
                  key={i}
                  onClick={
                    onRowClick
                      ? () => onRowClick(row.publisherId ?? null, row.subIdHint)
                      : undefined
                  }
                  className={
                    onRowClick
                      ? 'cursor-pointer hover:bg-neon-magenta/5'
                      : undefined
                  }
                  title={
                    isUnregistered
                      ? `Unregistered sub_id: ${row.subIdHint} (clicks-only, no publisher record)`
                      : onRowClick
                        ? 'Click to see detailed breakdown'
                        : undefined
                  }
                >
                  <td
                    className={`font-mono text-xs ${
                      isUnregistered ? 'text-text-primary' : 'text-neon-cyan'
                    }`}
                  >
                    {name}
                  </td>
                  <td className="text-right numeric">
                    {formatNumber(row.clicksTotal)}
                  </td>
                  <td className="text-right numeric text-neon-cyan">
                    {formatNumber(row.clicksQuality)}
                  </td>
                  <td className="text-right numeric text-neon-magenta">
                    {formatNumber(row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-green">
                    {cr(row.clicksQuality, row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-yellow">
                    {formatCurrency(row.revenueGross)}
                  </td>
                </tr>
              );
            })}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td
                colSpan={6}
                className="text-center text-text-tertiary py-10 font-mono text-xs"
              >
                {search
                  ? `no match for "${search}"`
                  : 'no data yet — waiting for traffic...'}
              </td>
            </tr>
          )}
        </tbody>
        {/* Footer with totals — sums across whatever the current filter shows.
            When user types a search query, totals update to reflect ONLY the
            matched rows (the user's whole point of searching). Empty search =
            totals for all visible rows. Skipped during loading and empty state. */}
        {!isLoading && filtered.length > 0 && (
          <TotalsFooter
            colSpan={6}
            search={search}
            totalRows={filtered.length}
            allRows={data.length}
            cells={[
              {
                value: filtered.reduce((s, r) => s + r.clicksTotal, 0),
                format: 'number',
                accent: 'text-text-secondary',
              },
              {
                value: filtered.reduce((s, r) => s + r.clicksQuality, 0),
                format: 'number',
                accent: 'text-neon-cyan',
              },
              {
                value: filtered.reduce((s, r) => s + r.conversionsApproved, 0),
                format: 'number',
                accent: 'text-neon-magenta',
              },
              { value: '—', format: 'literal', accent: 'text-text-tertiary' },
              {
                value: filtered.reduce((s, r) => s + r.revenueGross, 0),
                format: 'currency',
                accent: 'text-neon-yellow',
              },
            ]}
          />
        )}
      </table>
    </div>
  );
}

// ─── TotalsFooter — reusable footer for any dimension table ────────────────
// Renders "TOTAL · N rows (M filtered)" label + numeric cells matching the
// table's column structure. Cell values come pre-computed from the parent
// (so filtering logic stays close to the data).
function TotalsFooter({
  colSpan,
  search,
  totalRows,
  allRows,
  cells,
}: {
  colSpan: number;
  search: string;
  totalRows: number;
  allRows: number;
  cells: Array<{
    value: number | string;
    format: 'number' | 'currency' | 'literal';
    accent: string;
  }>;
}) {
  return (
    <tfoot>
      <tr className="border-t-2 border-border-subtle bg-bg-tertiary/30">
        <td className="font-display text-[10px] uppercase tracking-wider text-text-secondary py-2">
          TOTAL
          {search
            ? ` · ${totalRows} of ${allRows} (filtered)`
            : ` · ${totalRows} rows`}
        </td>
        {cells.map((c, i) => (
          <td key={i} className={`text-right numeric font-mono ${c.accent}`}>
            {c.format === 'literal'
              ? c.value
              : c.format === 'currency'
                ? formatCurrency(typeof c.value === 'number' ? c.value : 0)
                : formatNumber(typeof c.value === 'number' ? c.value : 0)}
          </td>
        ))}
      </tr>
    </tfoot>
  );
}

// ─── Campaign table ───────────────────────────────────────────────────────────

function CampaignTable({
  data,
  campaigns,
  isLoading,
  search,
}: {
  data: ByCampaignRow[];
  campaigns: Campaign[];
  isLoading: boolean;
  search: string;
}) {
  const nameMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of campaigns) m.set(c.id, c.displayName);
    return m;
  }, [campaigns]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const name =
        row.campaignId != null
          ? (nameMap.get(row.campaignId) ?? `#${row.campaignId}`)
          : '—';
      return (
        name.toLowerCase().includes(q) ||
        String(row.campaignId ?? '').includes(q)
      );
    });
  }, [data, nameMap, search]);

  return (
    <div className="overflow-x-auto">
      <table className="table-cyber">
        <thead>
          <tr>
            <th>Campaign</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Quality</th>
            <th className="text-right">Convs</th>
            <th className="text-right">CR</th>
            <th className="text-right" title="Yang dibayar network ke kita">
              Revenue
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={6} />
            ))}
          {!isLoading &&
            filtered.map((row, i) => {
              const name =
                row.campaignId != null
                  ? (nameMap.get(row.campaignId) ?? `#${row.campaignId}`)
                  : '—';
              return (
                <tr key={i}>
                  <td className="font-display text-xs">{name}</td>
                  <td className="text-right numeric">
                    {formatNumber(row.clicksTotal)}
                  </td>
                  <td className="text-right numeric text-neon-cyan">
                    {formatNumber(row.clicksQuality)}
                  </td>
                  <td className="text-right numeric text-neon-magenta">
                    {formatNumber(row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-green">
                    {cr(row.clicksQuality, row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-yellow">
                    {formatCurrency(row.revenueGross)}
                  </td>
                </tr>
              );
            })}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td
                colSpan={6}
                className="text-center text-text-tertiary py-10 font-mono text-xs"
              >
                {search
                  ? `no match for "${search}"`
                  : 'no data yet — waiting for traffic...'}
              </td>
            </tr>
          )}
        </tbody>
        {!isLoading && filtered.length > 0 && (
          <TotalsFooter
            colSpan={6}
            search={search}
            totalRows={filtered.length}
            allRows={data.length}
            cells={[
              {
                value: filtered.reduce((s, r) => s + r.clicksTotal, 0),
                format: 'number',
                accent: 'text-text-secondary',
              },
              {
                value: filtered.reduce((s, r) => s + r.clicksQuality, 0),
                format: 'number',
                accent: 'text-neon-cyan',
              },
              {
                value: filtered.reduce((s, r) => s + r.conversionsApproved, 0),
                format: 'number',
                accent: 'text-neon-magenta',
              },
              { value: '—', format: 'literal', accent: 'text-text-tertiary' },
              {
                value: filtered.reduce((s, r) => s + r.revenueGross, 0),
                format: 'currency',
                accent: 'text-neon-yellow',
              },
            ]}
          />
        )}
      </table>
    </div>
  );
}

// ─── Offer table ──────────────────────────────────────────────────────────────

function OfferTable({
  data,
  offers,
  isLoading,
  search,
}: {
  data: ByOfferRow[];
  offers: Offer[];
  isLoading: boolean;
  search: string;
}) {
  const nameMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const o of offers) m.set(o.id, o.name);
    return m;
  }, [offers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const name =
        row.offerId != null
          ? (nameMap.get(row.offerId) ?? `#${row.offerId}`)
          : '—';
      return (
        name.toLowerCase().includes(q) || String(row.offerId ?? '').includes(q)
      );
    });
  }, [data, nameMap, search]);

  return (
    <div className="overflow-x-auto">
      <table className="table-cyber">
        <thead>
          <tr>
            <th>Offer</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Quality</th>
            <th className="text-right">Convs</th>
            <th className="text-right">CR</th>
            <th className="text-right" title="Yang dibayar network ke kita">
              Revenue
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={6} />
            ))}
          {!isLoading &&
            filtered.map((row, i) => {
              const name =
                row.offerId != null
                  ? (nameMap.get(row.offerId) ?? `#${row.offerId}`)
                  : '—';
              return (
                <tr key={i}>
                  <td className="font-display text-xs">{name}</td>
                  <td className="text-right numeric">
                    {formatNumber(row.clicksTotal)}
                  </td>
                  <td className="text-right numeric text-neon-cyan">
                    {formatNumber(row.clicksQuality)}
                  </td>
                  <td className="text-right numeric text-neon-magenta">
                    {formatNumber(row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-green">
                    {cr(row.clicksQuality, row.conversionsApproved)}
                  </td>
                  <td className="text-right numeric text-neon-yellow">
                    {formatCurrency(row.revenueGross)}
                  </td>
                </tr>
              );
            })}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td
                colSpan={6}
                className="text-center text-text-tertiary py-10 font-mono text-xs"
              >
                {search
                  ? `no match for "${search}"`
                  : 'no data yet — waiting for traffic...'}
              </td>
            </tr>
          )}
        </tbody>
        {!isLoading && filtered.length > 0 && (
          <TotalsFooter
            colSpan={6}
            search={search}
            totalRows={filtered.length}
            allRows={data.length}
            cells={[
              {
                value: filtered.reduce((s, r) => s + r.clicksTotal, 0),
                format: 'number',
                accent: 'text-text-secondary',
              },
              {
                value: filtered.reduce((s, r) => s + r.clicksQuality, 0),
                format: 'number',
                accent: 'text-neon-cyan',
              },
              {
                value: filtered.reduce((s, r) => s + r.conversionsApproved, 0),
                format: 'number',
                accent: 'text-neon-magenta',
              },
              { value: '—', format: 'literal', accent: 'text-text-tertiary' },
              {
                value: filtered.reduce((s, r) => s + r.revenueGross, 0),
                format: 'currency',
                accent: 'text-neon-yellow',
              },
            ]}
          />
        )}
      </table>
    </div>
  );
}

// ─── Country table ────────────────────────────────────────────────────────────

function CountryTable({
  data,
  isLoading,
  search,
}: {
  data: ByCountryRow[];
  isLoading: boolean;
  search: string;
}) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => (row.country ?? '').toLowerCase().includes(q));
  }, [data, search]);

  return (
    <div className="overflow-x-auto">
      <table className="table-cyber">
        <thead>
          <tr>
            <th>Country</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Convs</th>
            <th className="text-right">CR</th>
            <th className="text-right">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={5} />
            ))}
          {!isLoading &&
            filtered.map((row, i) => (
              <tr key={i}>
                <td>
                  <CountryFlag iso2={row.country} showCode size={20} />
                </td>
                <td className="text-right numeric">
                  {formatNumber(row.clicksTotal)}
                </td>
                <td className="text-right numeric text-neon-magenta">
                  {formatNumber(row.conversionsApproved)}
                </td>
                <td className="text-right numeric text-neon-green">
                  {cr(row.clicksTotal, row.conversionsApproved)}
                </td>
                <td className="text-right numeric text-neon-yellow">
                  {formatCurrency(row.revenueGross)}
                </td>
              </tr>
            ))}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td
                colSpan={5}
                className="text-center text-text-tertiary py-10 font-mono text-xs"
              >
                {search
                  ? `no match for "${search}"`
                  : 'no data yet — waiting for traffic...'}
              </td>
            </tr>
          )}
        </tbody>
        {!isLoading && filtered.length > 0 && (
          <TotalsFooter
            colSpan={5}
            search={search}
            totalRows={filtered.length}
            allRows={data.length}
            cells={[
              {
                value: filtered.reduce((s, r) => s + r.clicksTotal, 0),
                format: 'number',
                accent: 'text-text-secondary',
              },
              {
                value: filtered.reduce((s, r) => s + r.conversionsApproved, 0),
                format: 'number',
                accent: 'text-neon-magenta',
              },
              { value: '—', format: 'literal', accent: 'text-text-tertiary' },
              {
                value: filtered.reduce((s, r) => s + r.revenueGross, 0),
                format: 'currency',
                accent: 'text-neon-yellow',
              },
            ]}
          />
        )}
      </table>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function StatisticsPage() {
  // NOTE: reverted user-role auto-scoping per user request. Statistics is
  // a network-wide view for both admin and publisher — same data, same
  // dimensions, same default. /earnings is the dedicated personal view.
  const [range, setRange] = useState<Range>('today');
  const [customFrom, setCustomFrom] = useState(() => utcDateInput());
  const [customTo, setCustomTo] = useState(() => utcDateInput());
  const [dimension, setDimension] = useState<Dimension>('Publisher');
  // Single search box — filters whichever dimension table is currently shown.
  // Reset to empty when switching dimensions so users don't see an unexpected
  // "no match" message after toggling.
  const [search, setSearch] = useState('');
  // Modal target: `undefined` = closed. Object holds the (publisherId,
  // optional subIdHint) tuple so we can scope unlinked drill-downs to a
  // specific sub_id instead of aggregating all unlinked conversions.
  const [drilldownTarget, setDrilldownTarget] = useState<
    { publisherId: number | null; subIdHint?: string } | undefined
  >(undefined);
  const pollMult = usePollMultiplier();
  const customValid = customFrom <= customTo;
  const statsParams =
    range === 'custom' ? { range, from: customFrom, to: customTo } : { range };
  const rangeKey =
    range === 'custom' ? `${range}:${customFrom}:${customTo}` : range;

  // Overview KPIs
  const overview = useQuery({
    queryKey: ['stats-overview', rangeKey],
    queryFn: () =>
      apiClient.get<OverviewData>('/api/v1/stats/overview', statsParams),
    refetchInterval: range === 'today' ? 15_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  // Dimension-specific data.
  // BUGFIX: previously no refetchInterval — Statistics tables stayed frozen
  // until user changed range. Now auto-refresh on `today` at 15s cadence
  // (slightly slower than KPI cards' 15s to spread API load).
  // Each query is enabled only when its dimension is active so we don't burn
  // API quota refreshing 4 endpoints when only 1 is visible.
  const todayRefetch = range === 'today' ? 15_000 * pollMult : false;

  const byPublisher = useQuery({
    queryKey: ['stats-by-publisher', rangeKey],
    queryFn: () =>
      apiClient.get<ByPublisherRow[]>(
        '/api/v1/stats/by-publisher',
        statsParams,
      ),
    enabled: dimension === 'Publisher' && (range !== 'custom' || customValid),
    refetchInterval: dimension === 'Publisher' ? todayRefetch : false,
  });

  const byCampaign = useQuery({
    queryKey: ['stats-by-campaign', rangeKey],
    queryFn: () =>
      apiClient.get<ByCampaignRow[]>('/api/v1/stats/by-campaign', statsParams),
    enabled: dimension === 'Campaign' && (range !== 'custom' || customValid),
    refetchInterval: dimension === 'Campaign' ? todayRefetch : false,
  });

  const byOffer = useQuery({
    queryKey: ['stats-by-offer', rangeKey],
    queryFn: () =>
      apiClient.get<ByOfferRow[]>('/api/v1/stats/by-offer', statsParams),
    enabled: dimension === 'Offer' && (range !== 'custom' || customValid),
    refetchInterval: dimension === 'Offer' ? todayRefetch : false,
  });

  const byCountry = useQuery({
    queryKey: ['stats-by-country', rangeKey],
    queryFn: () =>
      apiClient.get<ByCountryRow[]>('/api/v1/stats/by-country', statsParams),
    enabled: dimension === 'Country' && (range !== 'custom' || customValid),
    refetchInterval: dimension === 'Country' ? todayRefetch : false,
  });

  // Name resolution
  // BUGFIX: was hitting /api/v1/{publishers,campaigns,offers} which are
  // admin-only. User role got 403 → name maps stayed empty → tables showed
  // "#4" instead of publisher names. Switched to /api/v1/lookups/* which
  // returns the minimal display fields needed (id + name/displayName) and
  // is accessible to both admin and user roles.
  const publishers = useQuery({
    queryKey: ['publishers-lookup'],
    queryFn: () => apiClient.get<Publisher[]>('/api/v1/lookups/publishers'),
    staleTime: 5 * 60_000,
  });

  const campaigns = useQuery({
    queryKey: ['campaigns-lookup'],
    queryFn: () => apiClient.get<Campaign[]>('/api/v1/lookups/campaigns'),
    staleTime: 5 * 60_000,
  });

  const offers = useQuery({
    queryKey: ['offers-lookup'],
    queryFn: () => apiClient.get<Offer[]>('/api/v1/lookups/offers'),
    staleTime: 5 * 60_000,
  });

  const kpi = overview.data ?? {
    clicksTotal: 0,
    clicksQuality: 0,
    conversionsApproved: 0,
    revenueGross: 0,
    payoutGross: 0,
    profit: 0,
    cr: 0,
  };

  const tableLoading =
    dimension === 'Publisher'
      ? byPublisher.isLoading
      : dimension === 'Campaign'
        ? byCampaign.isLoading
        : dimension === 'Offer'
          ? byOffer.isLoading
          : byCountry.isLoading;

  const dimIcon: Record<Dimension, ReactNode> = {
    Publisher: <Users size={14} />,
    Campaign: <Megaphone size={14} />,
    Offer: <Tag size={14} />,
    Country: <Globe size={14} />,
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
            <BarChart2 size={20} className="text-neon-cyan" />
            Statistics
          </h1>
          <p className="text-sm text-text-secondary font-mono">
            // detailed breakdown ·{' '}
            {range === 'custom' ? `${customFrom}..${customTo} UTC` : range}
          </p>
          {range === 'today' && (
            <p className="text-[10px] text-text-tertiary font-mono mt-1">
              <span className="text-neon-yellow">ⓘ</span> Today's clicks have
              ~30-60s AE ingestion lag. For sub-2s live stream, see{' '}
              <a href="/clicks" className="text-neon-cyan hover:underline">
                /clicks
              </a>
              .
            </p>
          )}
        </div>
        {/* Range selector */}
        <div className="flex flex-col sm:items-end gap-2">
          <div className="inline-flex bg-bg-secondary border border-border-subtle rounded-sm p-1">
            {ranges.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm transition-all ${
                  range === r
                    ? 'bg-neon-cyan/10 text-neon-cyan'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          {range === 'custom' && (
            <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="input-cyber h-9 w-36"
              />
              <span className="text-text-tertiary">to</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="input-cyber h-9 w-36"
              />
              {!customValid && (
                <span className="text-neon-red">from must be before to</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Clicks"
          value={formatNumber(kpi.clicksTotal)}
          icon={<Users size={16} />}
          accent="cyan"
        />
        <KpiCard
          label="Conversions"
          value={formatNumber(kpi.conversionsApproved)}
          icon={<Target size={16} />}
          accent="magenta"
        />
        <KpiCard
          label="Revenue"
          value={formatCurrency(kpi.revenueGross)}
          icon={<DollarSign size={16} />}
          accent="yellow"
        />
      </div>

      {/* Breakdown table */}
      <Card className="p-0">
        {/* Card header with dimension toggle + search */}
        <div className="px-5 py-4 border-b border-border-subtle flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h3 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2 shrink-0">
            {dimIcon[dimension]}
            Breakdown by {dimension}
          </h3>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end flex-1 min-w-0">
            {/* Search — filters the currently-active dimension table */}
            <div className="w-full sm:w-64">
              <Input
                placeholder={`Search ${dimension.toLowerCase()}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon={<Search size={14} />}
              />
            </div>
            {/* Dimension pill toggle. Resetting search keeps the UX predictable
                when switching dimensions — otherwise a stale query from one
                dimension would silently empty another. */}
            <div className="inline-flex bg-bg-tertiary border border-border-subtle rounded-sm p-0.5 gap-0.5">
              {dimensions.map((d) => (
                <button
                  key={d}
                  onClick={() => {
                    setDimension(d);
                    setSearch('');
                  }}
                  className={`px-3 py-1 text-[11px] font-display uppercase tracking-wider rounded-sm transition-all ${
                    dimension === d
                      ? 'bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/30'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table body — swaps by dimension */}
        {dimension === 'Publisher' && (
          <PublisherTable
            data={byPublisher.data ?? []}
            publishers={publishers.data ?? []}
            isLoading={tableLoading}
            onRowClick={(id, subIdHint) =>
              setDrilldownTarget({ publisherId: id, subIdHint })
            }
            search={search}
          />
        )}
        {dimension === 'Campaign' && (
          <CampaignTable
            data={byCampaign.data ?? []}
            campaigns={campaigns.data ?? []}
            isLoading={tableLoading}
            search={search}
          />
        )}
        {dimension === 'Offer' && (
          <OfferTable
            data={byOffer.data ?? []}
            offers={offers.data ?? []}
            isLoading={tableLoading}
            search={search}
          />
        )}
        {dimension === 'Country' && (
          <CountryTable
            data={byCountry.data ?? []}
            isLoading={tableLoading}
            search={search}
          />
        )}
      </Card>

      <SubIdDetailModal
        publisherId={drilldownTarget?.publisherId}
        subIdHint={drilldownTarget?.subIdHint}
        onClose={() => setDrilldownTarget(undefined)}
        range={range}
        customFrom={customFrom}
        customTo={customTo}
      />
    </div>
  );
}
